/**
 * Utility functions for unit gallery import: folder discovery and mapping file load/save.
 */

/** Mapping file format: folder name -> array of unit IDs */
export type UnitGalleryMappings = Record<string, string[]>;

const MAPPINGS_FILENAME = 'unit-gallery-mappings.json';

/**
 * Fetch the list of unit-gallery folder names.
 * Expects a JSON file at /unit-gallery-folders.json (generated by scripts/list-unit-gallery-folders.ts).
 * Returns empty array if fetch fails (e.g. file not generated yet).
 */
export async function loadFolderList(): Promise<string[]> {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const res = await fetch(`${base}/unit-gallery-folders.json`, {
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = (await res.json()) as { folders?: string[] };
    return Array.isArray(data.folders) ? data.folders : [];
  } catch {
    return [];
  }
}

/**
 * Parse mapping file content (JSON string) into UnitGalleryMappings.
 */
export function parseMappingsJson(jsonString: string): UnitGalleryMappings {
  const raw = JSON.parse(jsonString) as unknown;
  if (raw === null || typeof raw !== 'object') return {};
  const out: UnitGalleryMappings = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof key !== 'string') continue;
    if (Array.isArray(value) && value.every((v) => typeof v === 'string')) {
      out[key] = value as string[];
    }
  }
  return out;
}

/**
 * Trigger download of the mapping file so the user can save it to data/master/unit-gallery-mappings.json.
 */
export function saveMappingsAsDownload(mappings: UnitGalleryMappings): void {
  const json = JSON.stringify(mappings, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = MAPPINGS_FILENAME;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Validate mappings: ensure keys are non-empty and values are arrays of non-empty strings.
 * Empty arrays are allowed (unmapped folders).
 */
export function validateMappings(mappings: UnitGalleryMappings): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  for (const [folderName, unitIds] of Object.entries(mappings)) {
    if (!folderName.trim()) {
      errors.push('Empty folder name in mappings');
    }
    if (!Array.isArray(unitIds)) {
      errors.push(`Folder "${folderName}" has invalid value (must be array)`);
    } else if (unitIds.some((id) => typeof id !== 'string' || !id.trim())) {
      errors.push(`Folder "${folderName}" has invalid unit ID(s)`);
    }
  }
  return {
    valid: errors.length === 0,
    errors,
  };
}
